import dayjs from 'dayjs';
import { useState } from 'react';

import { Box, Flex, Text } from '@mantine/core';
import { DatePicker, DatePickerInput, DatePickerInputProps } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';

import useAppStore from '@/shared/store/app.store';
import { TDateRange } from '@/shared/types/app.types';
import IconCalendar from '@/shared/uikit/assets/icons/IconCalendar.svg?react';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { LabelInput } from '@/shared/uikit/components/input';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';

import classes from './RangeDatePicker.module.css';

type DatePickerProps = {
	value: TDateRange;
	onChange: (date: TDateRange) => void;
} & Omit<DatePickerInputProps, 'value' | 'onChange'>;

export const RangeDatePicker = ({
	value,
	onChange,
	valueFormat = 'DD MMM',
	minDate,
	maxDate,
	...props
}: DatePickerProps) => {
	const isMobile = useAppStore((state) => state.isMobile);
	const [pickerValue, setPickerValue] = useState(value);
	const [opened, { close, open }] = useDisclosure(false);

	const handleSetDate = () => {
		if (pickerValue[1]) {
			onChange([
				dayjs(pickerValue[0])
					.set('hour', dayjs(value[0]).hour())
					.set('minute', dayjs(value[0]).minute())
					.toDate(),
				dayjs(pickerValue[1])
					.set('hour', dayjs(value[1]).hour())
					.set('minute', dayjs(value[1]).minute())
					.toDate(),
			]);
		} else {
			onChange([
				dayjs(pickerValue[0])
					.set('hour', dayjs(value[0]).hour())
					.set('minute', dayjs(value[0]).minute())
					.toDate(),
				dayjs(pickerValue[0])
					.set('hour', dayjs(value[1]).hour())
					.set('minute', dayjs(value[1]).minute())
					.toDate(),
			]);
		}

		close();
	};

	const content = (
		<Flex direction="column" align="center" justify="center">
			<Flex align="center" gap={12} className={classes.calendarCurrentValues}>
				<LabelInput
					label="Начало"
					value={pickerValue[0] ? String(dayjs(pickerValue[0]).format('D MMMM')) : ''}
					placeholder="Не выбрано"
					readOnly
				/>
				<Box>-</Box>
				<LabelInput
					label="Конец"
					value={
						pickerValue[1] ?? pickerValue[0]
							? String(dayjs(pickerValue[1] ?? pickerValue[0]).format('D MMMM'))
							: ''
					}
					placeholder="Не выбрано"
					readOnly
				/>
			</Flex>
			<DatePicker
				classNames={{
					calendarHeader: classes.calendarHeader,
				}}
				value={pickerValue}
				minDate={minDate}
				maxDate={maxDate}
				onChange={setPickerValue}
				type="range"
				level="month"
				size="md"
				styles={{
					day: {
						borderRadius: 9999,
					},
				}}
			/>
		</Flex>
	);

	return (
		<>
			<DatePickerInput
				classNames={classes}
				onClick={open}
				value={value}
				type="range"
				valueFormat={valueFormat}
				label={
					<Text c="#667085" fz={12} fw={500} lh="normal" tt="uppercase">
						Дата
					</Text>
				}
				rightSection={<IconCalendar fontSize={18} />}
				placeholder="Не выбрано"
				radius="lg"
				readOnly
				{...props}
			/>
			{isMobile ? (
				<BottomSheet
					opened={opened}
					onClose={close}
					showHeader={false}
					buttons={[
						{
							children: 'Закрыть',
							variant: 'default',
							onClick: close,
						},
						{
							children: 'Выбрать',
							variant: 'filled',
							onClick: handleSetDate,
						},
					]}
				>
					{content}
				</BottomSheet>
			) : (
				<ModalSheet
					opened={opened}
					onClose={close}
					buttons={[
						{
							children: 'Закрыть',
							variant: 'default',
							onClick: close,
						},
						{
							children: 'Выбрать',
							variant: 'filled',
							onClick: handleSetDate,
						},
					]}
					withCloseButton={false}
					size="lg"
					centered
				>
					{content}
				</ModalSheet>
			)}
		</>
	);
};
